import {
  Checkbox,
  FormControl,
  FormError,
  Input,
} from '@alefeducation/ui-elements-library-core';
import styled from 'styled-components';
import { ChangeEvent, useEffect, useState } from 'react';
import { OrganisationSettingVM } from '../../../apollo/queries/organisations/organisationSetting.queries';
import { isValidNumber } from '../../../utils/Validators';
import { SETTINGS_INPUT_INVALID_MESSAGE } from '../../../utils/constants';
import { InfoTooltip } from '../../Common';
import appLocalStorage from '../../../utils/AppLocalStorage';

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  width: 80%;
  height: 40px;
  column-gap: 14px;

  // switch & title
  > div:first-child {
    max-width: 350px;
  }

  // input group
  > div:nth-child(2) {
    display: flex;
    align-items: center;
    column-gap: 8px;
    height: 40px;

    // input
    > div:first-child {
      width: 95px;
    }

    // error message
    > div:nth-child(2) {
      margin-top: 0;
    }
  }
`;

interface Props {
  showInput?: boolean;
  setting: OrganisationSettingVM;
  onChange: (setting: OrganisationSettingVM) => void;
}

const SettingItem = (props: Props) => {
  const {
    setting: { enabled, name: title, value, description, settingCode },
    setting,
    showInput = false,
    onChange,
  } = props;
  const [isChecked, setIsChecked] = useState(enabled);
  const [inputValue, setInputValue] = useState(value as string);

  useEffect(() => {
    setIsChecked(enabled);
  }, [enabled]);

  useEffect(() => {
    setInputValue(value as string);
  }, [value]);

  const onToggle = () => {
    const updatedValue = !isChecked;
    setIsChecked(updatedValue);
    onChange({
      ...setting,
      enabled: updatedValue,
    });
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);
    onChange({
      ...setting,
      value,
    });
  };

  const onKeyPress = (event: any) => {
    if (!isValidNumber(event.key)) {
      event.preventDefault();
    }
  };

  const hasError = () => inputValue.trim() === '' || !isValidNumber(inputValue);

  return (
    <StyledContainer data-testid="setting-item-container">
      <div>
        <span onClick={onToggle} data-testid="switch-container">
          <Checkbox
            value=""
            variant="switch"
            disabled={appLocalStorage.isViewOnlyRole()}
            checked={isChecked}
            onChange={() => {}}
          >
            {title}
          </Checkbox>
        </span>
      </div>
      {isChecked && showInput && (
        <FormControl appearance={hasError() ? 'error' : 'regular'}>
          <Input
            onKeyPress={onKeyPress}
            data-testid="setting-input"
            className="setting-input"
            value={inputValue}
            onChange={onInputChange}
          />
          {hasError() && (
            <FormError>{SETTINGS_INPUT_INVALID_MESSAGE}</FormError>
          )}
        </FormControl>
      )}
      <InfoTooltip id={settingCode} text={description} />
    </StyledContainer>
  );
};

export default SettingItem;
